import React, { useState, useEffect } from 'react';
import { EditPage } from '../../../components/crud/EditPage';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Spinner } from '../../../components/ui/spinner';
import { useToast } from '../../../components/ui/use-toast';
import { useShow, useUpdate, useSelect } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { MODALITIES, CONNECTION_TYPES } from './ImagingDeviceCreate';

export const ImagingDeviceEdit: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const { query } = useShow({ resource: 'devices/imaging', id });
  const { mutate, isLoading: saving } = useUpdate();
  const { options: connectorOptions } = useSelect({ resource: 'devices/connectors', optionLabel: 'name', optionValue: 'id' });
  const record = query?.data?.data;

  const [values, setValues] = useState({
    name: '', manufacturer: '', model: '', serial_number: '', modality: '', category: '',
    connector_device: '', connection_type: 'dicom', dicom_enabled: true,
    device_ip: '', device_port: '104', ae_title: '', status: 'active',
    description: '', qc_enabled: false, qc_frequency_hours: '168',
    calibration_due_date: '', maintenance_due_date: '',
  });
  const set = (f: string, v: any) => setValues(p => ({ ...p, [f]: v }));

  useEffect(() => {
    if (!record) return;
    const cc = record.connection_config || {};
    setValues({
      name: record.name || '', manufacturer: record.manufacturer || '', model: record.model || '',
      serial_number: record.serial_number || '', modality: record.modality || '', category: record.category || '',
      connector_device: record.connector_device ? String(record.connector_device) : '',
      connection_type: record.connection_type || 'dicom', dicom_enabled: !!record.dicom_enabled,
      device_ip: record.device_ip || '', device_port: record.device_port?.toString() || '104',
      ae_title: record.ae_title || '', status: record.status || 'active',
      description: cc.description || '',
      qc_enabled: !!record.qc_enabled, qc_frequency_hours: record.qc_frequency_hours?.toString() || '168',
      calibration_due_date: record.calibration_due_date ? record.calibration_due_date.slice(0, 10) : '',
      maintenance_due_date: record.maintenance_due_date ? record.maintenance_due_date.slice(0, 10) : '',
    });
  }, [record]);

  if (query?.isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;

  const handleSave = () => {
    const connection_config: Record<string, any> = { ...(record?.connection_config || {}) };
    if (values.description) connection_config.description = values.description;
    mutate({ resource: 'devices/imaging', id: id as string, values: {
      name: values.name, manufacturer: values.manufacturer, model: values.model,
      serial_number: values.serial_number, modality: values.modality, category: values.category,
      connector_device: values.connector_device || undefined, connection_type: values.connection_type,
      dicom_enabled: values.dicom_enabled, status: values.status,
      device_ip: values.device_ip || undefined,
      device_port: values.device_port ? Number(values.device_port) : undefined,
      ae_title: values.ae_title, connection_config,
      qc_enabled: values.qc_enabled, qc_frequency_hours: Number(values.qc_frequency_hours),
      calibration_due_date: values.calibration_due_date || undefined,
      maintenance_due_date: values.maintenance_due_date || undefined,
    }}, {
      onSuccess: () => { toast({ title: 'Imaging device updated' }); navigate('/devices/imaging'); },
      onError: (e: any) => toast({ variant: 'destructive', title: 'Failed to update imaging device', description: e?.message }),
    });
  };

  return (
    <EditPage title="Edit Imaging Device" footerButtons={<Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>}>
      <div className="space-y-6">
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Device Name <span className="text-destructive">*</span></Label><Input value={values.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Modality <span className="text-destructive">*</span></Label>
              <Select value={values.modality} onValueChange={v => set('modality', v)}><SelectTrigger><SelectValue placeholder="Select modality" /></SelectTrigger>
                <SelectContent>{MODALITIES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Manufacturer</Label><Input value={values.manufacturer} onChange={e => set('manufacturer', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Model</Label><Input value={values.model} onChange={e => set('model', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Serial Number</Label><Input value={values.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Category (label)</Label><Input value={values.category} onChange={e => set('category', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={values.description} onChange={e => set('description', e.target.value)} /></div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Connector & Connection</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Connector Device <span className="text-destructive">*</span></Label>
              <Select value={values.connector_device} onValueChange={v => set('connector_device', v)}><SelectTrigger><SelectValue placeholder="Select connector" /></SelectTrigger>
                <SelectContent>{(connectorOptions || []).map((o: any) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Connection Type</Label>
              <Select value={values.connection_type} onValueChange={v => set('connection_type', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONNECTION_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">DICOM Configuration</h4>
          <div className="flex items-center gap-2"><Switch checked={values.dicom_enabled} onCheckedChange={v => set('dicom_enabled', v)} /><Label>DICOM-enabled (sends to connector SCP)</Label></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Calling AE Title</Label><Input maxLength={16} value={values.ae_title} onChange={e => set('ae_title', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Device IP</Label><Input value={values.device_ip} onChange={e => set('device_ip', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Device Port</Label><Input type="number" min={1} max={65535} value={values.device_port} onChange={e => set('device_port', e.target.value)} /></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Status & Maintenance</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={values.status} onValueChange={v => set('status', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem><SelectItem value="error">Error</SelectItem><SelectItem value="offline">Offline</SelectItem></SelectContent></Select></div>
            <div className="flex items-center gap-2 pt-6"><Switch checked={values.qc_enabled} onCheckedChange={v => set('qc_enabled', v)} /><Label>QC Enabled</Label></div>
            <div className="space-y-1.5"><Label>QC Frequency (Hours)</Label><Input type="number" min={1} value={values.qc_frequency_hours} onChange={e => set('qc_frequency_hours', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Calibration Due Date</Label><Input type="date" value={values.calibration_due_date} onChange={e => set('calibration_due_date', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Maintenance Due Date</Label><Input type="date" value={values.maintenance_due_date} onChange={e => set('maintenance_due_date', e.target.value)} /></div>
          </div>
        </CardContent></Card>
      </div>
    </EditPage>
  );
};

export default ImagingDeviceEdit;
